/**
 * Remembers the open 1:1 room for a study twin so Results → Chat reopens that
 * room instead of creating a new greeting-only room on every click.
 *
 * Each twin is its own localStorage entry. A shared JSON array loses mappings
 * when two tabs read the same snapshot and write it back. Writes,
 * token-conditional deletes, and legacy migration go through one index lock
 * so those mutations are atomic across tabs. `openStudyTwinChat` still holds
 * a per-twin lock across verify and create, and refuses closed, group, or
 * other-study rooms.
 */

import type { ChatRoomDto } from '@digital-twins/shared-types'

const LEGACY_STORAGE_KEY = 'dt.chat.oneOnOne.v1'
const ENTRY_PREFIX = 'dt.chat.oneOnOne.v1:'
const INDEX_LOCK_NAME = 'dt-chat-one-on-one-index'
const MAX_ENTRIES = 40

export interface OneOnOneRoomKey {
  populationId: string
  personaId: string
  experimentId: string
}

export interface OneOnOneRoomMemory extends OneOnOneRoomKey {
  token: string
  savedAt: number
}

function storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

export function oneOnOneRoomKey(key: OneOnOneRoomKey): string {
  return `${key.experimentId}\n${key.populationId}\n${key.personaId}`
}

function entryStorageKey(key: OneOnOneRoomKey): string {
  return ENTRY_PREFIX + encodeURIComponent(oneOnOneRoomKey(key))
}

function isMemory(entry: unknown): entry is OneOnOneRoomMemory {
  if (!entry || typeof entry !== 'object') return false
  const row = entry as Partial<OneOnOneRoomMemory>
  return (
    typeof row.token === 'string'
    && typeof row.populationId === 'string'
    && typeof row.personaId === 'string'
    && typeof row.experimentId === 'string'
    && typeof row.savedAt === 'number'
  )
}

function readRaw(store: Storage, id: string): OneOnOneRoomMemory | null {
  const raw = store.getItem(id)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    return isMemory(parsed) ? parsed : null
  } catch {
    return null
  }
}

function copyLegacyEntry(store: Storage, entry: OneOnOneRoomMemory): void {
  const id = entryStorageKey(entry)
  if (store.getItem(id)) {
    return
  }
  // Re-read at the write. A replacement published during the first read stays.
  if (store.getItem(id)) {
    return
  }
  store.setItem(id, JSON.stringify(entry))
}

/**
 * Copies the combined index into per-twin keys. Caller holds the index lock.
 * The blob stays when a copy throws, so a later pass can finish the rest.
 */
function migrateLegacy(store: Storage): void {
  const raw = store.getItem(LEGACY_STORAGE_KEY)
  if (!raw) {
    return
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    try {
      store.removeItem(LEGACY_STORAGE_KEY)
    } catch {
      // ignore
    }
    return
  }
  if (!Array.isArray(parsed)) {
    try {
      store.removeItem(LEGACY_STORAGE_KEY)
    } catch {
      // ignore
    }
    return
  }
  try {
    for (const entry of parsed) {
      if (!isMemory(entry)) {
        continue
      }
      copyLegacyEntry(store, entry)
    }
  } catch {
    return
  }
  try {
    store.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    // ignore
  }
}

function listRooms(store: Storage): Array<{ id: string; room: OneOnOneRoomMemory }> {
  const rooms: Array<{ id: string; room: OneOnOneRoomMemory }> = []
  for (let index = 0; index < store.length; index += 1) {
    const id = store.key(index)
    if (!id || !id.startsWith(ENTRY_PREFIX)) {
      continue
    }
    const room = readRaw(store, id)
    if (room) {
      rooms.push({ id, room })
    }
  }
  return rooms
}

function evictOldest(store: Storage, keepId: string): void {
  const rooms = listRooms(store)
  if (rooms.length <= MAX_ENTRIES) return
  const oldestFirst = [...rooms].sort((a, b) => a.room.savedAt - b.room.savedAt)
  const extra = rooms.length - MAX_ENTRIES
  let removed = 0
  for (const row of oldestFirst) {
    if (removed >= extra) {
      return
    }
    if (row.id === keepId) {
      continue
    }
    const fresh = readRaw(store, row.id)
    if (!fresh || fresh.savedAt !== row.room.savedAt || fresh.token !== row.room.token) {
      continue
    }
    try {
      store.removeItem(row.id)
      removed += 1
    } catch {
      // ignore
    }
  }
}

function writeRoom(entry: OneOnOneRoomKey & { token: string }, now = Date.now()): void {
  if (!entry.token || !entry.populationId || !entry.personaId || !entry.experimentId) {
    return
  }
  const store = storage()
  if (!store) {
    return
  }
  const record: OneOnOneRoomMemory = { ...entry, savedAt: now }
  const id = entryStorageKey(entry)
  try {
    store.setItem(id, JSON.stringify(record))
    evictOldest(store, id)
  } catch {
    // ignore quota / private mode
  }
}

function readRoom(key: OneOnOneRoomKey): OneOnOneRoomMemory | null {
  const store = storage()
  if (!store) {
    return null
  }
  return readRaw(store, entryStorageKey(key))
}

function removeRoom(key: OneOnOneRoomKey): void {
  const store = storage()
  if (!store) {
    return
  }
  try {
    store.removeItem(entryStorageKey(key))
  } catch {
    // ignore
  }
}

const localTails = new Map<string, Promise<unknown>>()

function enqueueLocal<T>(name: string, task: () => Promise<T>): Promise<T> {
  const previous = localTails.get(name) ?? Promise.resolve()
  const run = previous.catch(() => undefined).then(task)
  // The tail is set before `task` can await, so two callers in one turn queue.
  localTails.set(name, run)
  return run.finally(() => {
    if (localTails.get(name) === run) localTails.delete(name)
  })
}

function lockName(key: OneOnOneRoomKey): string {
  return `dt-chat-one-on-one:${key.experimentId}:${key.populationId}:${key.personaId}`
}

async function withNamedLock<T>(name: string, task: () => T | Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (locks) {
    return locks.request(name, () => task())
  }
  return enqueueLocal(name, async () => task())
}

/**
 * Serializes mutations of the room index across tabs. Held only around the
 * storage read/write, never across GET or create. Legacy migration runs
 * here so a publish cannot land between the empty-slot check and the copy.
 */
export function withOneOnOneIndexLock<T>(task: () => T | Promise<T>): Promise<T> {
  return withNamedLock(INDEX_LOCK_NAME, async () => {
    const store = storage()
    if (store) {
      migrateLegacy(store)
    }
    return task()
  })
}

/**
 * Holds verify/create for one study twin across tabs.
 * `navigator.locks` is the cross-tab lock. Callers without it (tests, older
 * engines) share an in-process queue for the same key.
 */
export function withOneOnOneRoomLock<T>(
  key: OneOnOneRoomKey,
  task: () => Promise<T>,
): Promise<T> {
  return withNamedLock(lockName(key), task)
}

export function rememberOneOnOneRoom(entry: OneOnOneRoomKey & { token: string }, now = Date.now()): void {
  writeRoom(entry, now)
}

/** Publishes a room the chat page is showing. Does not wait out an in-flight verify. */
export async function commitOneOnOneRoom(
  entry: OneOnOneRoomKey & { token: string },
  now = Date.now(),
): Promise<void> {
  await withOneOnOneIndexLock(() => {
    writeRoom(entry, now)
  })
}

export async function recallOneOnOneRoom(key: OneOnOneRoomKey): Promise<OneOnOneRoomMemory | null> {
  return withOneOnOneIndexLock(() => readRoom(key))
}

export function forgetOneOnOneRoom(key: OneOnOneRoomKey): void {
  removeRoom(key)
}

export type OneOnOneVerifySettlement = 'reused' | 'replaced' | 'dropped'

/**
 * After GET returns, keep a replacement token this verification did not
 * check. Delete only when the stored token is still the one that failed.
 */
export async function settleVerifiedOneOnOneRoom(
  key: OneOnOneRoomKey,
  verifiedToken: string,
  reusable: boolean,
): Promise<OneOnOneVerifySettlement> {
  return withOneOnOneIndexLock(() => {
    const current = readRoom(key)
    if (!current || current.token !== verifiedToken) return 'replaced'
    if (reusable) {
      writeRoom({ ...key, token: verifiedToken }, current.savedAt)
      return 'reused'
    }
    removeRoom(key)
    return 'dropped'
  })
}

/**
 * Stores `token` only when this twin has no room yet. A replacement written
 * while create was in flight wins, and the caller navigates to that token.
 */
export async function publishCreatedOneOnOneRoom(
  key: OneOnOneRoomKey,
  token: string,
  now = Date.now(),
): Promise<string> {
  return withOneOnOneIndexLock(() => {
    const current = readRoom(key)
    if (current?.token) {
      return current.token
    }
    writeRoom({ ...key, token }, now)
    return token
  })
}

export function isReusableOneOnOneRoom(
  room: ChatRoomDto,
  expected: OneOnOneRoomKey,
): boolean {
  if (room.status === 'closed' || room.status === 'error') return false
  if (room.populationId !== expected.populationId) return false
  if (room.participants.length !== 1) return false
  if (room.participants[0]?.personaId !== expected.personaId) return false
  if (room.studyContext?.experimentId !== expected.experimentId) return false
  return true
}
